import { useEffect, useRef, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import { getKioskTemplateUsageStatus } from "./kioskTemplateUsage"
import { LogError } from "../../../helpers/logger"
import { GET_ORGANIZATIONS_BY_ENTERPRISE } from "../queries"

export default function useKioskTemplateUsageLock({
  currentTemplate,
  userCtx,
}) {
  const [loadEnterpriseOrganizations] = useLazyQuery(
    GET_ORGANIZATIONS_BY_ENTERPRISE,
    {
      fetchPolicy: "cache-and-network",
    }
  )
  const [isToggleLockActive, setIsToggleLockActive] = useState(false)
  const [toggleLockReason, setToggleLockReason] = useState(null)
  const [kioskFormsUsingTemplate, setKioskFormsUsingTemplate] = useState([])
  const [noticeOrganizations, setNoticeOrganizations] = useState([])
  const [isKioskUsageCheckComplete, setIsKioskUsageCheckComplete] =
    useState(false)

  const hasCheckedKioskUsage = useRef(false)

  useEffect(() => {
    // Skip if already checked or required data is missing.
    if (hasCheckedKioskUsage.current || !currentTemplate || !userCtx) {
      return
    }

    hasCheckedKioskUsage.current = true

    const checkTemplateUsage = async () => {
      try {
        // Only kiosk-enabled templates are checked by the helper.
        const usageStatus = await getKioskTemplateUsageStatus({
          kiosk: currentTemplate.kiosk,
          templateName: currentTemplate.template_name,
          organizationId: userCtx.session.user.organization_id,
          enterpriseId: userCtx.session.user.enterprise_id,
          getKioskToken: userCtx.tokens.getKioskToken,
        })

        // Fail closed when the helper indicates usage could not be verified.
        if (usageStatus.shouldCheck && usageStatus.reason) {
          throw new Error(
            `Unable to verify Kiosk template usage: ${usageStatus.reason}`
          )
        }

        if (usageStatus.isInUse) {
          const formsUsingTemplate = usageStatus.formsUsingTemplate || []
          // Case-insensitive text compare so org/form ordering is predictable.
          const compareText = (a = "", b = "") =>
            String(a).localeCompare(String(b), undefined, {
              sensitivity: "base",
            })
          const organizationName = (organization) =>
            organization?.organization_name || organization?.name || ""
          const formName = (form) => form?.name || form?.id || ""

          // Keep availability/kiosk toggles read-only when the template is in use.
          setIsToggleLockActive(true)
          setToggleLockReason("in_use")

          // Resolve org names from enterprise scope to avoid incomplete staff-scoped lists.
          const response = await loadEnterpriseOrganizations({
            variables: {
              staff_id: userCtx.session.user.staff_id,
              enterprise_id: userCtx.session.user.enterprise_id,
            },
          })

          const organizations = response?.data?.organizationsByEnterpriseUid || []
          // Sort organizations once and reuse this order for the notice list.
          const sortedOrganizations = [...organizations].sort((a, b) =>
            compareText(organizationName(a), organizationName(b))
          )

          if (sortedOrganizations.length > 0) {
            setNoticeOrganizations(sortedOrganizations)
          }

          // Build a fast lookup so forms can be sorted by resolved org label.
          const organizationLabelById = new Map(
            sortedOrganizations.map((organization) => {
              const organizationId =
                organization.organization_id ||
                organization.organizationId ||
                organization.id
              return [String(organizationId || ""), organizationName(organization)]
            })
          )

          // Sort forms by organization label, then by form/template name.
          const sortedForms = [...formsUsingTemplate].sort((a, b) => {
            const organizationIdA = String(a?.soarOrganizationId || "unknown")
            const organizationIdB = String(b?.soarOrganizationId || "unknown")

            const organizationSort = compareText(
              organizationLabelById.get(organizationIdA) || organizationIdA,
              organizationLabelById.get(organizationIdB) || organizationIdB
            )

            if (organizationSort !== 0) return organizationSort
            return compareText(formName(a), formName(b))
          })

          setKioskFormsUsingTemplate(sortedForms)
        }
      } catch (error) {
        // Fail closed for these toggles when usage cannot be verified.
        setIsToggleLockActive(true)
        setToggleLockReason("check_failed")
        LogError(error)
      } finally {
        setIsKioskUsageCheckComplete(true)
      }
    }

    checkTemplateUsage()
  }, [currentTemplate, loadEnterpriseOrganizations, userCtx])

  return {
    isToggleLockActive,
    toggleLockReason,
    kioskFormsUsingTemplate,
    noticeOrganizations,
    isKioskUsageCheckComplete,
  }
}
